import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Shield, Download, Trash2, Loader2, Lock, Eye, Brain, Share2, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SETTINGS_CONFIG = [
  { key: 'data_collection', label: 'Data Collection', desc: 'Allow collection of your learning activity data to personalize your experience', icon: Eye },
  { key: 'analytics_tracking', label: 'Analytics Tracking', desc: 'Help improve our platform by sharing anonymous usage statistics', icon: Eye },
  { key: 'ai_processing', label: 'AI Processing', desc: 'Allow AI to analyze your data for personalized recommendations', icon: Brain },
  { key: 'third_party_sharing', label: 'Third-Party Sharing', desc: 'Share data with external educational partners', icon: Share2 },
  { key: 'marketing_emails', label: 'Marketing Communications', desc: 'Receive educational offers and updates via email', icon: Mail },
];

const CompliancePanel = ({ userRole }) => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [overview, setOverview] = useState(null);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/compliance/privacy-settings`);
      setSettings(res.data.settings);
    } catch (err) { /* silent */ }
  }, []);

  const fetchOverview = useCallback(async () => {
    if (userRole !== 'superuser') return;
    try {
      const res = await axios.get(`${API}/compliance/superuser/overview`);
      setOverview(res.data);
    } catch (err) { /* silent */ }
  }, [userRole]);

  useEffect(() => { fetchSettings(); fetchOverview(); }, [fetchSettings, fetchOverview]);

  const handleToggle = async (key, value) => {
    const updated = { ...settings, [key]: value };
    setSettings(updated);
    setSaving(true);
    try {
      await axios.put(`${API}/compliance/privacy-settings`, updated);
      toast.success('Privacy setting updated');
    } catch (err) { toast.error('Failed to update setting'); }
    finally { setSaving(false); }
  };

  const handleExportData = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/compliance/export-data`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'my-data-export.json';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Data exported successfully');
    } catch (err) { toast.error('Failed to export data'); }
    finally { setDownloading(false); }
  };

  const handleDeleteRequest = async () => {
    setDeleting(true);
    try {
      await axios.post(`${API}/compliance/request-deletion`);
      toast.success('Deletion request submitted. You will be contacted within 30 days.');
      setShowDeleteDialog(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit request'); }
    finally { setDeleting(false); }
  };

  return (
    <div className="space-y-6" data-testid="compliance-panel">
      <div className="flex items-center gap-2 mb-2">
        <Shield className="w-5 h-5 text-blue-600" />
        <h3 className="font-semibold text-lg">Privacy & Compliance</h3>
      </div>

      {/* Superuser Overview */}
      {userRole === 'superuser' && overview && (
        <Card className="p-4 bg-blue-50 border-blue-200">
          <h4 className="font-semibold text-blue-800 mb-3">Compliance Overview</h4>
          <div className="grid grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-700">{overview.total_users}</p>
              <p className="text-xs text-blue-600">Total Users</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-700">{overview.compliance_rate}%</p>
              <p className="text-xs text-green-600">Consent Rate</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-amber-700">{overview.pending_deletion_requests}</p>
              <p className="text-xs text-amber-600">Pending Deletions</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-700">{overview.total_consents}</p>
              <p className="text-xs text-purple-600">Total Consents</p>
            </div>
          </div>
        </Card>
      )}

      {/* Privacy Settings */}
      {settings && (
        <Card className="p-4">
          <h4 className="font-semibold mb-4 flex items-center gap-2">
            <Lock className="w-4 h-4" /> Privacy Preferences
          </h4>
          <div className="space-y-4">
            {SETTINGS_CONFIG.map(({ key, label, desc, icon: Icon }) => (
              <div key={key} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50" data-testid={`privacy-${key}`}>
                <Icon className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <Label className="font-medium">{label}</Label>
                  <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                </div>
                <Switch
                  checked={settings[key] || false}
                  onCheckedChange={v => handleToggle(key, v)}
                  disabled={saving}
                />
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Data Rights */}
      <Card className="p-4">
        <h4 className="font-semibold mb-3">Your Data Rights (GDPR / COPPA)</h4>
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={handleExportData} disabled={downloading} data-testid="export-data-btn">
            {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Download My Data
          </Button>
          <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => setShowDeleteDialog(true)} data-testid="delete-data-btn">
            <Trash2 className="w-4 h-4 mr-2" /> Request Data Deletion
          </Button>
        </div>
        <p className="text-xs text-gray-400 mt-3">
          Under GDPR and COPPA regulations, you have the right to access, export, and request deletion of your personal data.
        </p>
      </Card>

      {/* Delete Confirmation */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle className="text-red-600">Request Data Deletion</DialogTitle></DialogHeader>
          <div className="py-4 space-y-3">
            <p className="text-sm text-gray-700">This will submit a request to delete all your personal data from our platform. This action:</p>
            <ul className="text-sm text-gray-600 list-disc pl-5 space-y-1">
              <li>Will be processed within 30 days</li>
              <li>Is irreversible once completed</li>
              <li>Will remove all your learning progress, submissions, and profile data</li>
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteRequest} disabled={deleting} data-testid="confirm-delete-btn">
              {deleting ? 'Submitting...' : 'Confirm Deletion Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompliancePanel;
